import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, MapIcon } from 'lucide-react';
import { usePower } from '../../contexts/PowerContext';
import { powerLocationById } from '../../data/power';
import { countdownLabel, freshnessOf, journeyVerdict } from '../../utils/power';
import { ConfidenceLine, FreshnessLine, IconTile, LOCATION_ICON, PowerStatusPill, PowerSkeleton, powerIcons } from './PowerPrimitives';
import type { PowerStatus } from '../../types/power';
export const BEFORE_YOU_GO_ART = 'https://cdn.magicpatterns.com/patterns/generated-images/693e37ca-b6bd-4515-96bc-de67acf9d603.jpg';

/**
 * Before you go — the emotional centre of Power.
 *
 * It answers one question in one glance: is there light where I am going? The
 * three rows are the person's own places in the order the journey happens, and
 * the verdict beneath them states the weakest thing found rather than averaging
 * into something reassuring.
 *
 * The card is deliberately tinted rather than sitting on the same white as the
 * panels beside it. On a page where nine sections compete, the one carrying the
 * answer has to win on surface as well as position.
 */
export function BeforeYouGo() {
  const {
    homeLocationId,
    campusLocationId,
    next,
    getLocationStatus,
    offline
  } = usePower();
  const rows = [{
    id: homeLocationId,
    label: 'Your accommodation'
  }, {
    id: campusLocationId,
    label: 'Campus'
  }, ...(next?.locationId ? [{
    id: next.locationId,
    label: 'Next class'
  }] : [])];
  const statuses = rows.map(row => getLocationStatus(row.id));
  const verdict = journeyVerdict(statuses);

  /**
   * Three genuinely different situations, which must not collapse into one
   * another: there is no class left this week; there is a class but its venue is
   * not a mapped Power location; or there is a mapped destination.
   */
  const destination = !next ? 'none' as const : next.locationId ? 'mapped' as const : 'unmapped' as const;
  const heading = next ? next.laterInWeek ? `Your next class is ${next.day}, ${next.start}` : `Your next class starts in ${countdownLabel(next.startsInMinutes)}` : 'No upcoming class found';

  // Anything beyond the configured freshness ladder is stale, and stale evidence
  // is stated as such instead of being presented as the current situation.
  const stale = statuses.some(status => status.state !== 'unknown' && freshnessOf(status.lastConfirmedAt) === 'stale');
  return <section aria-labelledby="before-you-go-heading" className="relative overflow-hidden rounded-2xl border border-brand-100 bg-brand-50/60 shadow-panel" data-id="element-4137" data-mp="th93h8bi00g5">
      {/* The illustration sits behind the content on wide cards and below the
          rows on narrow ones, so it never competes with the answer. */}
      <img src={BEFORE_YOU_GO_ART} alt="" aria-hidden className="pointer-events-none absolute bottom-0 right-0 hidden h-[78%] w-auto select-none object-contain object-bottom opacity-95 sm:block" data-id="element-4138" data-mp="2iumdne6akip6" />
      <div className="relative p-5 sm:p-6" data-id="element-4139" data-mp="2mfo3y5wv33zv">
        <div className="max-w-[62%]" data-id="element-4140" data-mp="2rd7k5bd4dqug">
          <h2 id="before-you-go-heading" className="text-h4 font-semibold text-ink" data-id="element-4141" data-mp="2re927zsjzdlo">
            Before you go
          </h2>
          <p className="mt-0.5 text-body-sm text-ink-muted" data-id="element-4142" data-mp="2chrvxmz80p5z">{heading}</p>
          {destination === 'mapped' && next && <p className="mt-1 truncate text-caption font-medium text-ink-subtle" data-id="element-4143" data-mp="353jbh81vrlkj">
              {next.courseCode} · {next.title}
            </p>}
        </div>

        <ul className="mt-4 max-w-[26rem] space-y-2.5" data-id="element-4144" data-mp="3a50bz2ggd60l">
          {rows.map((row, index) => {
          const location = powerLocationById(row.id);
          const status = statuses[index];
          if (!location) return null;
          return <li key={`${row.id}-${row.label}`} className="flex items-center gap-3" data-id="element-4145" data-mp="3o25ckmxedcog" data-mpi={index}>
                <IconTile name={LOCATION_ICON[location.kind]} size="sm" data-id="element-4146" data-mp="3stx9df75cen1" />
                <div className="min-w-0 flex-1" data-id="element-4147" data-mp="2552djo0t7fpw">
                  <p className="truncate text-body-sm font-medium text-ink" data-id="element-4148" data-mp="256wwiwsisxrc">
                    {row.label === 'Next class' ? location.name : row.label}
                  </p>
                  {status.state === 'unknown' ? <p className="text-caption text-ink-subtle" data-id="element-4149" data-mp="1v8z9udqb790u">No recent reports</p> : status.confidence === 'high' ? <ConfidenceLine confidence={status.confidence} data-id="element-4150" data-mp="2nw3ak6idd16t" /> : <FreshnessLine at={status.lastConfirmedAt} data-id="element-4151" data-mp="2iw77c9hhxdsb" />}
                </div>
                <PowerStatusPill state={status.state} data-id="element-4152" data-mp="2e58ker205hg3" />
              </li>;
        })}
        </ul>

        {/* A class whose venue is not a mapped location. Naming the venue is
            honest; guessing at the nearest plausible building would not be. */}
        {destination === 'unmapped' && next && <p className="mt-3 max-w-[26rem] rounded-lg border border-line bg-surface px-3 py-2 text-caption text-ink-muted" data-id="element-4153" data-mp="31ut7nm56v0v6">
            We know your class is at <span className="font-medium text-ink" data-id="element-4154" data-mp="3e5u59j9dot77">{next.venue}</span>
            , but it isn’t mapped to a Power location yet, so we can’t say what the power is
            doing there.
          </p>}

        {destination === 'none' && <p className="mt-3 max-w-[26rem] text-caption text-ink-muted" data-id="element-4155" data-mp="3o4tassyuk8pz">
            You can still check the power around you.
          </p>}

        {stale && <p className="mt-3 max-w-[26rem] rounded-lg border border-warn-100 bg-warn-50 px-3 py-2 text-caption text-warn-800" data-id="element-4156" data-mp="yiraqc8fz9x">
            Some of this was last confirmed over an hour ago. It may no longer be the current
            situation.
          </p>}

        {offline && <p className="mt-3 max-w-[26rem] rounded-lg border border-warn-100 bg-warn-50 px-3 py-2 text-caption text-warn-800" data-id="element-4157" data-mp="foq8z3uf1e9k">
            Connection unavailable. Showing the last known power status.
          </p>}

        {/* On narrow screens the illustration moves here, at a size that does not
            eat the screen — the answer stays above it. */}
        <img src={BEFORE_YOU_GO_ART} alt="A student walking across campus" className="mt-4 h-32 w-full rounded-xl object-cover object-center sm:hidden" data-id="element-4158" data-mp="3m3m6oepz1a39" />

        <div className="mt-5 flex flex-wrap items-center justify-between gap-3" data-id="element-4159" data-mp="3w1jvb3bqt8we">
          <p className={`text-body font-semibold ${verdict.tone === 'ok' ? 'text-ok-800' : verdict.tone === 'warn' ? 'text-warn-800' : 'text-ink-muted'}`} data-id="element-4160" data-mp="dnsolvfzrqk3">
            {verdict.message}
          </p>
          {destination === 'none' ? <Link to="/power/map" className="inline-flex min-h-touch items-center gap-2 rounded-xl bg-brand-700 px-4 py-2.5 text-body-sm font-semibold text-white transition-colors duration-150 ease-out hover:bg-brand-800 focus-visible:outline-none" data-id="element-4161" data-mp="2peeieyxywmvn">
              <MapIcon className="h-4 w-4" aria-hidden data-id="element-4162" data-mp="14xnf4vu7v5v7" />
              View power map
            </Link> : <Link to="/power/before-you-go" className="inline-flex min-h-touch items-center gap-2 rounded-xl bg-brand-700 px-4 py-2.5 text-body-sm font-semibold text-white transition-colors duration-150 ease-out hover:bg-brand-800 focus-visible:outline-none" data-id="element-4163" data-mp="q77w9np9wa07">
              View details
              <ArrowRightIcon className="h-4 w-4" aria-hidden data-id="element-4164" data-mp="lahhd3p7fkgd" />
            </Link>}
        </div>
      </div>
    </section>;
}
export function BeforeYouGoSkeleton() {
  return <div className="rounded-2xl border border-brand-100 bg-brand-50/60 p-5 shadow-panel sm:p-6" data-id="element-4165" data-mp="c900vk7vxgr5">
      <PowerSkeleton className="h-6 w-40" data-id="element-4166" data-mp="21nxuokngfxj9" />
      <PowerSkeleton className="mt-2 h-4 w-56" data-id="element-4167" data-mp="26mhk4v19brgd" />
      <div className="mt-5 space-y-3" data-id="element-4168" data-mp="26l6aiel4rphc">
        <PowerSkeleton className="h-11 w-full max-w-[26rem]" data-id="element-4169" data-mp="26k4z2907d0z3" />
        <PowerSkeleton className="h-11 w-full max-w-[26rem]" data-id="element-4170" data-mp="tf4s8xek2izi" />
        <PowerSkeleton className="h-11 w-full max-w-[26rem]" data-id="element-4171" data-mp="pdj9wwj95chx" />
      </div>
      <PowerSkeleton className="mt-5 h-11 w-40" data-id="element-4172" data-mp="kncmh00h7y19" />
    </div>;
}

/** One reusable status row, used by the overview and the full-screen view. */
export function PowerStatusRow({
  label,
  status,
  icon
}: {
  label: string;
  status: PowerStatus;
  icon: keyof typeof powerIcons;
}) {
  return <div className="flex items-center gap-3" data-id="element-4173" data-mp="amsivqphihfv">
      <IconTile name={icon} size="sm" data-id="element-4174" data-mp="5p0mokht495v" />
      <div className="min-w-0 flex-1" data-id="element-4175" data-mp="5s6bdymix912">
        <p className="truncate text-body-sm font-medium text-ink" data-id="element-4176" data-mp="1nypsabd4972">{label}</p>
        <FreshnessLine at={status.lastConfirmedAt} data-id="element-4177" data-mp="3surpf9qic09h" />
      </div>
      <PowerStatusPill state={status.state} data-id="element-4178" data-mp="3swm9ymxv9qd1" />
    </div>;
}